import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { companyInfo } from '../data/mockData';
import { toast } from 'sonner';

export const Contacto = () => {
  const location = useLocation();
  const productPreselected = location.state?.product || '';

  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    productoInteres: productPreselected,
    mensaje: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('nombre', formData.nombre);
      payload.append('empresa', formData.empresa || '');
      payload.append('email', formData.email);
      payload.append('telefono', formData.telefono || '');
      payload.append('productoInteres', formData.productoInteres || '');
      payload.append('mensaje', formData.mensaje);

      const response = await fetch('/enviar-solicitud.php', {
        method: 'POST',
        body: payload,
        credentials: 'same-origin'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("¡Solicitud Enviada!", {
          description: data.message || "Nos pondremos en contacto contigo en las próximas 24 horas.",
        });
        setFormData({
          nombre: '',
          empresa: '',
          email: '',
          telefono: '',
          productoInteres: '',
          mensaje: ''
        });
      } else {
        toast.error("Error al enviar", {
          description: data.error || "Ocurrió un problema. Intenta de nuevo o contáctanos por WhatsApp.",
        });
      }
    } catch (error) {
      toast.error("Error de conexión", {
        description: "No pudimos enviar tu solicitud. Por favor contáctanos por WhatsApp.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      `Hola MAESI, me interesa información sobre: ${formData.productoInteres || 'sus productos y servicios'}`
    );
    window.open(`https://wa.me/${companyInfo.contact.whatsapp.replace(/\s/g, '')}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-blue-300">Contáctanos</span>
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Estamos listos para asesorarte y diseñar la solución perfecta para tu empresa
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Solicitar Cotización
                </h2>
                <p className="text-gray-600 mb-8">
                  Completa el formulario y nos pondremos en contacto contigo
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-semibold text-gray-900 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="empresa" className="block text-sm font-semibold text-gray-900 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                        placeholder="tu@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-semibold text-gray-900 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                        placeholder="+52 33 1234 5678"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="productoInteres" className="block text-sm font-semibold text-gray-900 mb-2">
                      Producto de Interés
                    </label>
                    <select
                      id="productoInteres"
                      name="productoInteres"
                      value={formData.productoInteres}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                    >
                      <option value="">Selecciona un producto</option>
                      <option value="Llenadoras Industriales">Llenadoras Industriales</option>
                      <option value="Etiquetadoras Automáticas">Etiquetadoras Automáticas</option>
                      <option value="Agitadores Industriales">Agitadores Industriales</option>
                      <option value="Transportadores Industriales">Transportadores Industriales</option>
                      <option value="Tanques de Proceso">Tanques de Proceso</option>
                      <option value="Proyecto Personalizado">Proyecto Personalizado</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-900 mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors resize-none"
                      placeholder="Cuéntanos sobre tu proyecto o necesidad..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Enviar Solicitud
                      </>
                    )}
                  </button>
                </form>

                {/* WhatsApp Alternative */}
                <div className="mt-8 pt-8 border-t-2 border-gray-100">
                  <p className="text-center text-gray-600 mb-4">O contáctanos directamente por:</p>
                  <button
                    onClick={handleWhatsAppClick}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <MessageSquare className="w-5 h-5" />
                    WhatsApp: {companyInfo.contact.whatsappDisplay}
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Cards */}
              <div className="grid gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Dirección</h3>
                      <p className="text-gray-600 leading-relaxed">
                        {companyInfo.location.fullAddress}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Teléfonos</h3>
                      <a href={`tel:${companyInfo.contact.phone}`} className="text-blue-600 hover:text-blue-700 font-medium block">
                        {companyInfo.contact.phone}
                      </a>
                      <a href={`tel:${companyInfo.contact.phone2}`} className="text-blue-600 hover:text-blue-700 font-medium block">
                        {companyInfo.contact.phone2}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                      <a href={`mailto:${companyInfo.contact.email}`} className="text-blue-600 hover:text-blue-700 font-medium break-all">
                        {companyInfo.contact.email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-600">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Horario</h3>
                      <p className="text-gray-600">Lunes a Viernes</p>
                      <p className="text-gray-600">9:00 AM - 6:30 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white p-4 rounded-xl shadow-lg">
                <h3 className="font-bold text-gray-900 mb-4">Ubicación</h3>
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    title="Ubicación MAESI"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3732.8!2d-103.3921!3d20.6597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjDCsDM5JzM1LjAiTiAxMDPCsDIzJzMxLjYiVw!5e0!3m2!1ses!2smx!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    className="rounded-lg"
                  ></iframe>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-8 rounded-xl shadow-xl text-white">
                <CheckCircle className="w-12 h-12 mb-4" />
                <h3 className="text-2xl font-bold mb-3">Respuesta Garantizada</h3>
                <p className="text-blue-100 leading-relaxed">
                  Nos comprometemos a responder tu solicitud en menos de 24 horas hábiles. Tu proyecto es nuestra prioridad.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
